// @ts-nocheck

import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import { getOutcomeFromProps, getOutcomeStatusFromProps } from '../../selectors/_propsGetters_';
import { selectEntitiesBets } from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesCategories } from '../../selectors/entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesMatches } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesTournaments } from '../../selectors/entities/tournaments/entitiesTournamentsPrimary.selector';
import { type BetState } from '../../states/entities/bets';
import { OutcomeEntity } from '../../states/entities/outcomes';
import { CardModes } from '../../types/common';
import { type CardMode } from '../../types/common';
import { OutcomeStatuses } from '../../types/history';
import { type OutcomeStatus } from '../../types/history';
import { type Selector, type SelectorMaker } from '../../types/selectors';
import { SportIds } from '../../types/sports';

// Todo: distinguer boosted / bigBoosted
const getCardModeFromSportId = (sportId) => {
  switch (sportId) {
    case SportIds.SPECIALS__99999:
      return CardModes.BOOSTED;
    case SportIds.SPECIAL_ODDS:
      return CardModes.BOOSTED;
    default:
      return CardModes.NORMAL;
  }
};

type SubOutcomeType = {
  outcomeLabel: string;
  outcomeStatus: OutcomeStatus;
  betTitle: string;
  outcomeId?: number;
};

type HistoryItemOutcomeContentFactoryPropsType = {
  betTitle?: string;
  outcomeStatus: OutcomeStatus;
  outcomeLabel: string;
  isMyMatch: boolean;
  cardMode?: CardMode;
  flagId?: string;
  subOutcomes?: Array<SubOutcomeType>;
};

const makeHistoryItemOutcomeContentFactoryPropsSelector: SelectorMaker<
  HistoryItemOutcomeContentFactoryPropsType
> = () => {
  const selectIsMyMatch: Selector<boolean> = createSelector([getOutcomeFromProps], (outcome) => !!outcome?.subOutcomes);

  const selectSubOutcomes: Selector<Array<SubOutcomeType> | undefined> = createSelector(
    [selectIsMyMatch, getOutcomeFromProps, getOutcomeStatusFromProps, selectEntitiesOutcomes, selectEntitiesBets],
    (isMyMatch, outcome, status, outcomesMap, betsMap) => {
      if (isMyMatch) {
        return outcome.subOutcomes.map((subOutcomeId) => {
          if (Array.isArray(subOutcomeId)) {
            // Cas betbuilder multichance
            const itemOutcomes = subOutcomeId.map((outcomeId) => outcomesMap[outcomeId]);
            const outcomeLabel = itemOutcomes.map((outcome) => outcome.label).join(` ${__('or')} `);
            const betIds = itemOutcomes.map((outcome) => outcome.betId);
            const uniqBetIds = [...new Set([...betIds])];
            const uniqBetId = uniqBetIds[0];
            const { betTitle } = betsMap[uniqBetId];
            const outcomeHasOccured = itemOutcomes.reduce((acc, cur) => acc || cur.hasOccured, false);
            const isCancelled = itemOutcomes.reduce((acc, cur) => acc && cur.isCancelled, true);

            let outcomeStatus = status;

            // Le cas par defaut est cancelled / undefined.
            if (!outcomeStatus) {
              if (isCancelled) {
                outcomeStatus = OutcomeStatuses.CANCELLED;
              } else {
                outcomeStatus = OutcomeStatuses.RUNNING;
                if (outcomeHasOccured === true) {
                  outcomeStatus = OutcomeStatuses.WON;
                } else if (outcomeHasOccured === false) {
                  outcomeStatus = OutcomeStatuses.LOST;
                }
              }
            }

            return {
              betTitle,
              outcomeStatus,
              outcomeLabel,
            };
          } else {
            // Cas betbuilder classique
            const subOutcome = outcomesMap[subOutcomeId];
            const subBet = betsMap[subOutcome?.betId];

            return {
              betTitle: subBet?.betTitle,
              outcomeStatus: OutcomeEntity.getStatus(subOutcome) ?? status,
              outcomeLabel: subOutcome?.label,
              outcomeId: subOutcomeId,
            };
          }
        });
      }
    },
  );

  const selectOutcomeBet: Selector<BetState | undefined> = createSelector(
    [getOutcomeFromProps, selectEntitiesBets],
    (outcome, betsMap) => betsMap[outcome?.betId],
  );

  const selectBetTitle: Selector<string | undefined> = createSelector([selectOutcomeBet], (bet) => bet?.betTitle);

  const selectOutcomeStatus: Selector<OutcomeStatus | undefined> = createSelector(
    [getOutcomeStatusFromProps, getOutcomeFromProps],
    (status, outcome) => status ?? OutcomeEntity.getStatus(outcome),
  );

  const selectOutcomeLabel: Selector<string | undefined> = createSelector(
    [getOutcomeFromProps],
    (outcome) => outcome?.label,
  );

  const selectCardMode: Selector<CardMode> = createSelector(
    [
      selectEntitiesBets,
      selectEntitiesCategories,
      selectEntitiesTournaments,
      selectEntitiesMatches,
      getOutcomeFromProps,
    ],
    (betsMap, categoriesMap, tournamentsMap, matchesMap, outcome) => {
      const bet = betsMap[outcome?.betId];
      const match = matchesMap[bet?.matchId];
      const tournament = tournamentsMap[match?.tournamentId];
      const category = categoriesMap[tournament?.categoryId];
      const sportId = category?.sportId;

      return getCardModeFromSportId(sportId);
    },
  );

  const selectFlagId: Selector<string | undefined> = createSelector([getOutcomeFromProps], (outcome) => outcome?.flag);

  return createSelector(
    [
      selectBetTitle,
      selectOutcomeStatus,
      selectOutcomeLabel,
      selectIsMyMatch,
      selectCardMode,
      selectFlagId,
      selectSubOutcomes,
    ],
    (betTitle, outcomeStatus, outcomeLabel, isMyMatch, cardMode, flagId, subOutcomes) => ({
      betTitle,
      outcomeStatus,
      outcomeLabel,
      isMyMatch,
      cardMode,
      flagId,
      subOutcomes,
    }),
  );
};

export default makeHistoryItemOutcomeContentFactoryPropsSelector;
